import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { InterviewTemplateSelectService } from '@core/interview-results-legacy';
import { InterviewTemplateScope } from '@core/interview-templates-data';
import { CandidateInterviewTemplatesService } from '@core/interview-templates-logic';
import { OfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import type { SelectGroupedOptions } from '@core/user-interface-legacy';
import { deepEqual } from '@core/utils-legacy';

@Injectable()
export class AddEvaluationDataService {
  private readonly store = inject(Store);
  private readonly interviewTemplateSelectService = inject(InterviewTemplateSelectService);
  private readonly candidateInterviewTemplatesService = inject(CandidateInterviewTemplatesService);

  public readonly currentOffer = signal<OfferSelectOption | undefined>(undefined);
  public readonly firstOffer = signal<OfferSelectOption | undefined>(undefined);
  public readonly candidateId = signal<number | undefined>(undefined);
  public readonly isTalentPool = computed(() => this.currentOffer()?.kind === OfferKind.TalentPool);

  public readonly canManageInterviewTemplates = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageInterviewTemplates),
  );
  public readonly allGroupedEvaluationForms = toSignal(
    this.interviewTemplateSelectService.getInterviewTemplatesSelectOptions(
      InterviewTemplateScope.Interview,
    ),
    { initialValue: [] },
  );

  public readonly templatesState = toSignal(
    toObservable(
      computed(() => ({
        candidateId: this.candidateId(),
        offer: this.currentOffer(),
        fallbackGroups: this.allGroupedEvaluationForms(),
      })),
    ).pipe(
      distinctUntilChanged(deepEqual),
      switchMap(({ candidateId, offer, fallbackGroups }) =>
        this.fetchCandidateTemplatesState(candidateId, offer, fallbackGroups),
      ),
    ),
    { initialValue: { groups: [], preselectedTemplateId: null, loaded: false } },
  );
  public readonly firstOfferTemplatesState = toSignal(
    toObservable(
      computed(() => ({
        candidateId: this.candidateId(),
        offer: this.firstOffer(),
        fallbackGroups: [],
      })),
    ).pipe(
      distinctUntilChanged(deepEqual),
      switchMap(({ candidateId, offer, fallbackGroups }) =>
        this.getCandidateTemplatesState(candidateId, offer, fallbackGroups),
      ),
    ),
    { initialValue: { groups: [], preselectedTemplateId: null, loaded: false } },
  );

  public readonly visibleTemplates = computed(() => this.templatesState().groups);
  public readonly templatesLoaded = computed(() => this.templatesState().loaded);
  public readonly preselectedTemplateId = computed(
    () => this.templatesState().preselectedTemplateId,
  );

  public readonly firstOfferTemplatesLoaded = computed(
    () => this.firstOfferTemplatesState().loaded,
  );
  public readonly recommendedTemplate = computed(() => {
    const { preselectedTemplateId, groups } = this.firstOfferTemplatesState();

    if (preselectedTemplateId === null) {
      return undefined;
    }

    return (<SelectGroupedOptions[]>groups)
      .flatMap(group => group.options)
      .find(option => option.value === preselectedTemplateId);
  });

  private fetchCandidateTemplatesState(
    candidateId: number | undefined,
    offer: OfferSelectOption | undefined,
    fallbackGroups: SelectGroupedOptions[],
  ) {
    const offerId = offer ? Number(offer.id) : null;

    if (offer?.kind !== OfferKind.TalentPool && offerId && candidateId) {
      return combineLatest({
        result: this.candidateInterviewTemplatesService.fetchGroupedCandidateTemplates({
          candidateId,
          offerId,
          kind: 'interview',
        }),
        loaded: this.candidateInterviewTemplatesService.areCandidateTemplatesLoaded({
          candidateId,
          offerId,
          kind: 'interview',
        }),
      }).pipe(
        map(({ result, loaded }) => ({
          groups: result.groups,
          preselectedTemplateId: result.preselectedTemplateId,
          loaded,
        })),
      );
    }

    return of({ groups: fallbackGroups, preselectedTemplateId: null, loaded: true });
  }

  private getCandidateTemplatesState(
    candidateId: number | undefined,
    offer: OfferSelectOption | undefined,
    fallbackGroups: SelectGroupedOptions[],
  ) {
    const offerId = offer ? Number(offer.id) : null;

    if (offer?.kind !== OfferKind.TalentPool && offerId && candidateId) {
      return combineLatest({
        result: this.candidateInterviewTemplatesService.getGroupedCandidateTemplates({
          candidateId,
          offerId,
          kind: 'interview',
        }),
        loaded: this.candidateInterviewTemplatesService.areCandidateTemplatesLoaded({
          candidateId,
          offerId,
          kind: 'interview',
        }),
      }).pipe(
        map(({ result, loaded }) => ({
          groups: result.groups,
          preselectedTemplateId: result.preselectedTemplateId,
          loaded,
        })),
      );
    }

    return of({ groups: fallbackGroups, preselectedTemplateId: null, loaded: true });
  }
}
